/**
 * Mention & Tagging System Types
 *
 * Firestore Collection: mentions/{mentionId}
 *
 * Supports @mentions in notes/updates across deals, projects, and faults.
 * Users get real-time notifications when they're mentioned.
 */

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Entity types that support mentions
 */
export enum MentionEntityType {
  DEAL = 'DEAL',
  PROJECT = 'PROJECT',
  FAULT = 'FAULT',
  TODO = 'TODO',
  JOB = 'JOB',
  BUG_FIX = 'BUG_FIX',
  SERVICE_REQUEST = 'SERVICE_REQUEST',
  VEHICLE = 'VEHICLE',
  LEAVE_REQUEST = 'LEAVE_REQUEST',
  TIMESHEET = 'TIMESHEET',
  MEETING = 'MEETING',
  CUSTOMER = 'CUSTOMER',
}

/**
 * Mention notification status
 */
export enum MentionStatus {
  UNREAD = 'UNREAD',
  READ = 'READ',
  ARCHIVED = 'ARCHIVED',
}

// ============================================================================
// INTERFACES
// ============================================================================

/**
 * Mention - A @mention notification linking a user to a note/update
 *
 * Created when:
 * - User adds a note with @username in deals/projects/faults
 * - User explicitly tags someone for attention
 *
 * Firestore path: mentions/{mentionId}
 */
export interface Mention {
  id?: string;
  tenantId: string;

  // Who was mentioned
  mentionedUserId: string;
  mentionedUserName: string;

  // Who created the mention
  createdBy: string;
  createdByName: string;

  // What entity this relates to
  entityType: MentionEntityType;
  entityId: string;
  entityTitle: string; // e.g., "Deal: John Smith - NBN Installation"

  // The note/comment content
  noteContent: string;
  noteExcerpt: string; // First 100 chars for preview

  // Metadata
  status: MentionStatus;
  createdAt: string; // ISO timestamp
  readAt?: string; // ISO timestamp when marked as read

  // Optional: Direct link for navigation
  linkPath?: string; // e.g., "/home/deals/abc123" or "/home/faults?openId=xyz789"

  // ID of the underlying note / timeline entry this mention came from.
  // Lets the click handler deep-link to the specific entry inside the entity
  // (appended as `?noteId=` on top of `linkPath`). Omitted for direct tags
  // that don't have an underlying note.
  sourceNoteId?: string;
}

/**
 * MentionableUser - User that can be @mentioned
 * Derived from team/users collection
 */
export interface MentionableUser {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  avatar?: string;
  isActive?: boolean;
}

/**
 * MentionInput - Parsed mention data from text input
 */
export interface MentionInput {
  userId: string;
  userName: string;
  startIndex: number;
  endIndex: number;
}

/**
 * NotificationSummary - Aggregated mention counts for notification bell
 */
export interface NotificationSummary {
  unreadCount: number;
  recentMentions: Mention[];
  byEntity: {
    deals: number;
    projects: number;
    faults: number;
    todos: number;
    jobs: number;
    bugFixes: number;
    vehicles: number;
    timesheets: number;
  };
}
